import { useEffect, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import { getCurrentUser } from "@/auth/firebase";
import { LIVEVIEW_URL } from "@/config.js";
import { masqueradeToken } from "@/util";

export function configureLiveView(
  conn: { send: (message: string) => void },
  searchString: string,
  filter: unknown,
  filterVersion: number,
  envID: string | undefined,
) {
  getCurrentUser()
    ?.getIdToken()
    .then((token) => {
      const msg = {
        action: "configure",
        token: token,
        email: getCurrentUser()?.email,
        search_string: searchString,
        filter: filter,
        filter_version: filterVersion,
        envID: envID,
      };

      if (masqueradeToken()) {
        (msg as { masquerade_token: string } & typeof msg).masquerade_token =
          masqueradeToken()!;
      }

      conn.send(JSON.stringify(msg));
    });
}

export type LiveViewEvent = {
  event_name: string;
  event_def_id?: string;
  type?: string;
  path?: string;
  distinct_id?: string;
  approximate_arrival_time?: number;
  library?: string;
  built_in_properties?: { host?: string };
};

/**
 * Streams every live event for an environment to `onEvent` until unmounted.
 * Pass `false` for `enabled` to keep the connection closed.
 */
export function useLiveEventStream(
  envID: string | undefined,
  onEvent: (event: LiveViewEvent) => void,
  enabled = true,
) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!envID || !enabled) {
      return;
    }
    const client = new ReconnectingWebSocket(LIVEVIEW_URL, [], {
      minReconnectionDelay: 5000,
      maxReconnectionDelay: 5000,
    });
    const configure = () =>
      configureLiveView(client, "", { type: "and", filters: [] }, 0, envID);
    client.onopen = configure;
    client.onmessage = (message) => {
      try {
        onEventRef.current(JSON.parse(message.data) as LiveViewEvent);
      } catch (error) {
        console.error("Unable to parse live view event", error);
      }
    };
    // The server drops idle connections, so keep re-sending the config as a
    // heartbeat, like the Live View page does.
    const heartbeat = window.setInterval(() => {
      if (client.readyState === ReconnectingWebSocket.OPEN) {
        configure();
      }
    }, 1000);
    return () => {
      window.clearInterval(heartbeat);
      client.close();
    };
  }, [envID, enabled]);
}
